import { AxiosResponse } from 'axios'
import apiClient from 'api/common/common'
import { AgreedStatus, BankAccount, BusinessForm, BusinessFormType, ContractStatusType } from 'api/common/constants'
import { ConnectorPostRequest, ConnectorPreRegisterRequest, ConnectorPutRequest } from 'api/common/requests'
import { ConnectorGetResponse, ConnectorPostResponse, ConnectorPreRegisterResponse, ConnectorPutResponse } from 'api/common/responses'
import { Status } from 'api/common/statuses'
import { ConsentQuestions } from 'utilities/resources/consentQuestions'
import { ConnectSubscriptionFormValues } from 'utilities/resources/formItemValues'
import loginData from 'utilities/loginData'

/**
 * コネクター情報取得
 * @param callback
 */
export const connectorGetAPI = async (callback: (status: Status, response: { formValues: ConnectSubscriptionFormValues; consentQuestion: ConsentQuestions }) => void) => {
  await apiClient
    .get('/connector')
    .then((response: AxiosResponse<ConnectorGetResponse>) => {
      const status = response?.headers['x-aqc-status'] ?? 'failed'
      const apiResult = response?.data ?? {}
      const formValues: ConnectSubscriptionFormValues = {
        countryNumber: apiResult.countryCode ?? '+81',
        phoneNumber: apiResult.tel ?? '',
        businessForm: `${apiResult.userType ?? BusinessForm.Individual}`,
        companyName: apiResult.companyName ?? '',
        companyNameKana: apiResult.companyNameKana ?? '',
        companyInvoiceNumber: apiResult.companyInvoiceNumber ?? '',
        lastName: apiResult.lastName ?? '',
        firstName: apiResult.firstName ?? '',
        lastNameKana: apiResult.lastNameKana ?? '',
        firstNameKana: apiResult.firstNameKana ?? '',
        lastNameAlphabet: apiResult.lastNameAlphabet ?? '',
        firstNameAlphabet: apiResult.firstNameAlphabet ?? '',
        gender: apiResult.gender ?? '',
        email: apiResult.email ?? '',
        birthday: apiResult.birthday ?? '',
        bankCode: apiResult.bankCode ?? '',
        bankName: apiResult.bankName ?? '',
        bankType: apiResult.accountType ?? BankAccount.Normal,
        bankBranchCode: apiResult.branchNumber ?? '',
        bankBranchName: apiResult.branchName ?? '',
        bankAccountNumber: apiResult.accountNumber ?? '',
        bankAccountName: apiResult.accountName ?? '',
        postalCode: apiResult.postCode ?? '',
        prefecture: String(apiResult.prefectureCode || ''),
        address: apiResult.addressDetail ?? '',
        receivePostalCode: apiResult.postCodeForMail ?? '',
        receivePrefecture: String(apiResult.prefectureCodeForMail || ''),
        receiveAddress: apiResult.addressDetailForMail ?? '',
      }
      const consentQuestions = apiResult.agreed ?? []
      const consentQuestion: ConsentQuestions = {
        consentQuestion1: `${consentQuestions[0] ?? AgreedStatus.No}`,
        consentQuestion2: `${consentQuestions[1] ?? AgreedStatus.No}`,
        consentQuestion3: `${consentQuestions[2] ?? AgreedStatus.No}`,
        consentQuestion4: `${consentQuestions[3] ?? AgreedStatus.No}`,
        consentQuestion5: `${consentQuestions[4] ?? AgreedStatus.No}`,
        consentQuestion6: `${consentQuestions[5] ?? AgreedStatus.No}`,
        consentQuestion7: `${consentQuestions[6] ?? AgreedStatus.No}`,
        consentQuestion8: `${consentQuestions[7] ?? AgreedStatus.No}`,
        consentQuestion9: `${consentQuestions[8] ?? AgreedStatus.No}`,
        consentQuestion10: `${consentQuestions[9] ?? AgreedStatus.No}`,
        consentQuestion11: `${consentQuestions[10] ?? AgreedStatus.No}`,
        consentQuestion12: `${consentQuestions[11] ?? AgreedStatus.No}`,
        consentQuestion13: `${consentQuestions[12] ?? AgreedStatus.No}`,
        consentQuestion14: `${consentQuestions[13] ?? AgreedStatus.No}`,
        consentQuestion15: `${consentQuestions[14] ?? AgreedStatus.No}`,
        consentQuestion16: `${consentQuestions[15] ?? AgreedStatus.No}`,
        consentQuestion17: `${consentQuestions[16] ?? AgreedStatus.No}`,
        consentQuestion18: `${consentQuestions[17] ?? AgreedStatus.No}`,
        consentQuestion19: `${consentQuestions[18] ?? AgreedStatus.No}`,
        consentQuestion20: `${consentQuestions[19] ?? AgreedStatus.No}`,
      }
      
      // contractStatusが含まれている場合はloginDataを更新
      if (apiResult.contractStatus != null) {
        loginData.setContractStatus(apiResult.contractStatus)
      }
      if (apiResult.userType != null) {
        loginData.setUserType(apiResult.userType)
      }
      
      callback(status, { formValues, consentQuestion })
    })
    .catch((error) => {
      console.error(error)
    })
}

/**
 * 仮登録API
 * @param request
 * @param callback
 */
export const connectorPreRegisterAPI = async (request: ConnectorPreRegisterRequest, callback: (status: Status, response: ConnectorPreRegisterResponse) => void) => {
  await apiClient
    .post('/connector/preRegister', request)
    .then((response: AxiosResponse<ConnectorPreRegisterResponse>) => {
      const status = response?.headers['x-aqc-status'] ?? 'failed'
      callback(status, response?.data)
    })
    .catch((error) => {
      console.error(error)
    })
}

/**
 * お申込み情報登録API
 * @param request
 * @param callback
 */
export const connectorContractAPI = async (
  request: ConnectorPostRequest,
  callback: (result: boolean, contractStatus?: ContractStatusType, userType?: BusinessFormType) => void,
) => {
  await apiClient
    .post('/connector', request)
    .then((response: AxiosResponse<ConnectorPostResponse>) => {
      const status = response?.headers['x-aqc-status'] ?? 'failed'
      const apiResult = response?.data?.result ?? false
      const result = status === Status.Success && apiResult
      const contractStatus = response?.data?.contractStatus
      const userType = response?.data?.userType
      if (result && contractStatus != null && userType != null) {
        loginData.setContractStatus(contractStatus)
        loginData.setUserType(userType)
      }
      callback(result, contractStatus, userType)
    })
    .catch((error) => {
      console.error(error)
    })
}

/**
 * コネクター情報変更API
 * @param request
 * @param callback
 */
export const connectorEditAPI = async (request: ConnectorPutRequest, callback: (result: boolean, contractStatus?: ContractStatusType, userType?: BusinessFormType) => void) => {
  await apiClient
    .put('/connector', request)
    .then((response: AxiosResponse<ConnectorPutResponse>) => {
      const status = response?.headers['x-aqc-status'] ?? 'failed'
      const apiResult = response?.data?.result ?? false
      const result = status === Status.Success && apiResult
      const contractStatus = response?.data?.contractStatus
      const userType = response?.data?.userType
      if (result && contractStatus != null && userType != null) {
        loginData.setContractStatus(contractStatus)
        loginData.setUserType(userType)
      }
      callback(result, contractStatus, userType)
    })
    .catch((error) => {
      console.error(error)
    })
}
